import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus, Sparkles } from "lucide-react";

import BetaBadge from "./BetaBadge";
import ChatComposer from "./ChatComposer";
import ChatContextBar from "./ChatContextBar";
import ChatEmptyState from "./ChatEmptyState";
import ChatThread from "./ChatThread";
import PromptGallery from "./PromptGallery";
import PromptPickerPopover from "./PromptPickerPopover";
import {
  CHAT_PROMPT_CUSTOM_TYPE,
  promptRequiresCallRecording,
} from "../constants/chatConstants";
import { useChatAgentLoopFlag } from "../hooks/useChatAgentLoopFlag";
import { useChatIntentSuggestionsFlag } from "../hooks/useChatIntentSuggestionsFlag";
import { useChatToolTraceFlag } from "../hooks/useChatToolTraceFlag";
import ToolConfirmationCard from "./ToolConfirmationCard";
import { useCallRecordings } from "../hooks/useCallRecordings";
import { useChatSend } from "../hooks/useChatSend";
import { useChatTurn } from "../hooks/useChatTurn";
import { useCrmChatSession } from "../hooks/useCrmChatSession";
import { useManagingPrompts } from "../hooks/useManagingPrompts";
import type { CrmChatRecordContext, ManagingPromptCatalogEntry } from "../types/chat.types";

/**
 * Workspace-wide chat page (route `/workspaces/:projectId/ask`).
 *
 * Users pick a CRM record and prompt from actionable context controls, then
 * run catalog prompts or free-form chat with the same flow as the record drawer.
 */
const CrmChatPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const workspaceId = projectId ?? "";

  const agentLoopEnabled = useChatAgentLoopFlag();
  const toolTraceEnabled = useChatToolTraceFlag();
  const intentSuggestionsEnabled = useChatIntentSuggestionsFlag();

  const [record, setRecord] = useState<CrmChatRecordContext | null>(null);
  const [activePrompt, setActivePrompt] = useState<ManagingPromptCatalogEntry | null>(null);
  const [composerText, setComposerText] = useState("");
  const [selectedCallRecordingId, setSelectedCallRecordingId] = useState<string | null>(null);

  const { sessionId, initialMessages, persist, clear } = useCrmChatSession({
    workspaceId,
    record,
  });

  const promptsQuery = useManagingPrompts(workspaceId, {
    recordId: record?.recordId ?? null,
    activityId: selectedCallRecordingId,
  });
  const promptCatalog: ManagingPromptCatalogEntry[] = useMemo(
    () => promptsQuery.data ?? [],
    [promptsQuery.data]
  );

  const callRecordingsQuery = useCallRecordings(workspaceId, record?.recordId);
  const callRecordings = callRecordingsQuery.data ?? [];
  const hasCallRecordingWithTranscript = callRecordings.some((r) => r.hasTranscript);
  const activePromptNeedsTranscript =
    activePrompt != null && promptRequiresCallRecording(activePrompt);
  const catalogExecuteReady =
    !activePromptNeedsTranscript || Boolean(selectedCallRecordingId);

  useEffect(() => {
    setSelectedCallRecordingId(null);
  }, [record?.recordId, activePrompt?.promptType]);

  useEffect(() => {
    if (!activePrompt || !promptRequiresCallRecording(activePrompt)) {
      return;
    }
    const withTranscript = callRecordings.filter((r) => r.hasTranscript);
    if (withTranscript.length === 0) {
      return;
    }
    setSelectedCallRecordingId((prev) => {
      if (prev && withTranscript.some((r) => r.activityId === prev)) {
        return prev;
      }
      return withTranscript[0]?.activityId ?? null;
    });
  }, [activePrompt, callRecordings]);

  const handleMessagesChange = useCallback(
    (messages: Parameters<typeof persist>[0]["messages"]) => {
      persist({
        messages,
        promptType: activePrompt?.promptType,
        promptName: activePrompt?.name,
      });
    },
    [persist, activePrompt?.promptType, activePrompt?.name]
  );

  const chatSend = useChatSend({
    active: !agentLoopEnabled,
    workspaceId,
    record,
    callRecordingActivityId: selectedCallRecordingId,
    initialMessages,
    onMessagesChange: handleMessagesChange,
  });
  const chatTurn = useChatTurn({
    active: agentLoopEnabled,
    workspaceId,
    sessionId,
    record,
    callRecordingActivityId: selectedCallRecordingId,
    initialMessages,
    onMessagesChange: handleMessagesChange,
  });
  const messages = agentLoopEnabled ? chatTurn.messages : chatSend.messages;
  const isSending = agentLoopEnabled ? chatTurn.isStreaming : chatSend.isSending;
  const error = agentLoopEnabled ? chatTurn.error : chatSend.error;
  const regenerate = agentLoopEnabled ? chatTurn.regenerate : chatSend.regenerate;

  const handleSend = useCallback(async () => {
    const trimmed = composerText.trim();
    if (trimmed === "/") return;
    if (!trimmed && (!activePrompt || activePrompt.promptType === CHAT_PROMPT_CUSTOM_TYPE)) {
      return;
    }
    setComposerText("");
    if (agentLoopEnabled) {
      await chatTurn.sendTurn({ content: trimmed });
      return;
    }
    await chatSend.send({
      content: trimmed || undefined,
      prompt: activePrompt ?? null,
      callRecordingActivityId: selectedCallRecordingId,
    });
  }, [activePrompt, agentLoopEnabled, chatSend, chatTurn, composerText, selectedCallRecordingId]);

  const handleAskDirectly = useCallback(async () => {
    const trimmed = composerText.trim();
    if (!trimmed) return;
    setActivePrompt(null);
    setComposerText("");
    if (agentLoopEnabled) {
      await chatTurn.sendTurn({ content: trimmed });
      return;
    }
    await chatSend.send({
      content: trimmed,
      prompt: null,
      callRecordingActivityId: selectedCallRecordingId,
    });
  }, [agentLoopEnabled, chatSend, chatTurn, composerText, selectedCallRecordingId]);

  const handleSelectPromptForComposer = useCallback((prompt: ManagingPromptCatalogEntry) => {
    setActivePrompt(prompt);
    setComposerText("");
  }, []);

  const handleSelectPrompt = useCallback(
    async (prompt: ManagingPromptCatalogEntry) => {
      setActivePrompt(prompt);
      if (prompt.promptType === CHAT_PROMPT_CUSTOM_TYPE) return;
      if (promptRequiresCallRecording(prompt)) {
        return;
      }
      const extra = composerText.trim() || undefined;
      if (agentLoopEnabled) {
        await chatTurn.runCatalogPrompt({
          prompt,
          content: extra,
          callRecordingActivityId: selectedCallRecordingId,
        });
      } else {
        await chatSend.send({
          prompt,
          content: extra,
          callRecordingActivityId: selectedCallRecordingId,
        });
      }
      setComposerText("");
    },
    [agentLoopEnabled, chatSend, chatTurn, composerText, selectedCallRecordingId]
  );

  const handleNewChat = () => {
    if (agentLoopEnabled) chatTurn.reset();
    else chatSend.reset();
    clear();
    setActivePrompt(null);
    setComposerText("");
    setSelectedCallRecordingId(null);
  };

  const contextPromptPicker = (
    <PromptPickerPopover
      prompts={promptCatalog}
      recordType={record?.recordType ?? null}
      onSelect={handleSelectPromptForComposer}
      hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
      trigger={
        <button
          type="button"
          disabled={isSending}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-blue-300 px-3 py-1 text-xs font-medium text-blue-700 hover:border-blue-400 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t("Choose a prompt")}
          <ChevronDown className="h-3 w-3" />
        </button>
      }
    />
  );

  return (
    <div className="flex h-full flex-col bg-gray-50">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
        <div className="inline-flex items-center gap-2 text-base font-semibold text-gray-900">
          <span>{t("Ask")}</span>
          <BetaBadge size="sm" />
        </div>
        <button
          type="button"
          onClick={handleNewChat}
          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
        >
          <Plus className="h-3.5 w-3.5" />
          {t("New chat")}
        </button>
      </div>

      <div className="flex flex-1 flex-col">
        <div className="mx-auto w-full max-w-3xl">
          <ChatContextBar
            record={record}
            workspaceId={workspaceId}
            onSelectRecord={setRecord}
            onClearRecord={() => setRecord(null)}
            promptType={activePrompt?.promptType ?? null}
            promptName={activePrompt?.name ?? null}
            onClearPrompt={() => setActivePrompt(null)}
            promptPickerSlot={contextPromptPicker}
            disabled={isSending}
            callRecordings={callRecordings}
            selectedCallRecordingId={selectedCallRecordingId}
            onSelectCallRecording={setSelectedCallRecordingId}
            callRecordingsLoading={callRecordingsQuery.isLoading}
            requiresCallRecordingWithTranscript={activePromptNeedsTranscript}
          />
        </div>

        <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col">
          <ChatThread
            messages={messages}
            record={record}
            isLoading={isSending && !agentLoopEnabled}
            isStreaming={agentLoopEnabled && chatTurn.isStreaming}
            streamingMessageId={chatTurn.streamingMessageId ?? undefined}
            showToolTrace={toolTraceEnabled}
            onRegenerate={regenerate}
            promptCatalog={promptCatalog}
            onSelectSuggestedPrompt={handleSelectPrompt}
            emptyState={
              <ChatEmptyState record={record}>
                <PromptGallery
                  prompts={promptCatalog}
                  recordType={record?.recordType ?? null}
                  isLoading={promptsQuery.isLoading}
                  onSelectPrompt={handleSelectPrompt}
                  hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
                  onSelectCustom={() => {
                    setActivePrompt({
                      promptType: CHAT_PROMPT_CUSTOM_TYPE,
                      name: t("Custom") as string,
                      description: "",
                      categories: [],
                      requiresRecord: false,
                    });
                  }}
                />
              </ChatEmptyState>
            }
          />

          {error && messages[messages.length - 1]?.role !== "system" ? (
            <div className="mx-4 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          ) : null}

          {agentLoopEnabled && chatTurn.pendingToolCall ? (
            <ToolConfirmationCard
              toolCall={chatTurn.pendingToolCall}
              turnId={chatTurn.activeTurnId}
              isSubmitting={chatTurn.confirmSubmitting}
              errorMessage={chatTurn.confirmError}
              onApprove={() => chatTurn.approvePendingTool()}
              onReject={() => chatTurn.rejectPendingTool()}
            />
          ) : null}

          <div className="relative z-20 shrink-0 px-4 pb-4">
            <ChatComposer
              value={composerText}
              onChange={setComposerText}
              onSend={handleSend}
              onStop={
                agentLoopEnabled && chatTurn.activeTurnId ? () => chatTurn.abortTurn() : undefined
              }
              disabled={Boolean(agentLoopEnabled && chatTurn.pendingToolCall)}
              isLoading={isSending}
              isStreaming={agentLoopEnabled && chatTurn.isStreaming}
              activePromptName={activePrompt?.name ?? null}
              onClearPrompt={() => setActivePrompt(null)}
              slashPrompts={promptCatalog}
              recordType={record?.recordType ?? null}
              onSelectPromptForComposer={handleSelectPromptForComposer}
              hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
              catalogExecuteReady={catalogExecuteReady}
              intentSuggestionsEnabled={intentSuggestionsEnabled}
              intentSuggestionPrompts={promptCatalog}
              showIntentSuggestionsWhenEmpty={messages.length === 0}
              onAskDirectly={handleAskDirectly}
              promptPickerSlot={
                <PromptPickerPopover
                  prompts={promptCatalog}
                  recordType={record?.recordType ?? null}
                  onSelect={handleSelectPrompt}
                  hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
                  trigger={
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                      aria-label={t("Choose a prompt") as string}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      {t("Prompts")}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  }
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmChatPage;
